import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, TextField, Button, Avatar, IconButton, Chip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { commentActions } from '../../store';
import { getAvatarColor } from '../../theme/tokens';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const CommentSection = ({ taskId }) => {
  const dispatch = useDispatch();
  const comments = useSelector((state) => state.comments.comments);
  const myProfile = useSelector((state) => state.auth.myProfile);
  const [newComment, setNewComment] = useState('');

  useEffect(() => {
    if (taskId) dispatch(commentActions.getComments(taskId));
  }, [dispatch, taskId]);

  const handleSubmit = () => {
    if (!newComment.trim()) return;
    dispatch(commentActions.createComment({ text: newComment.trim(), task: taskId }));
    setNewComment('');
  };

  const handleResolve = (comment) => {
    dispatch(commentActions.updateComment(comment._id, { resolved: !comment.resolved }));
  };

  const handleDelete = (id) => {
    dispatch(commentActions.deleteComment(id));
  };

  const unresolvedCount = (comments || []).filter((c) => !c.resolved).length;

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
          Comments ({(comments || []).length})
        </Typography>
        {unresolvedCount > 0 && (
          <Chip
            label={`${unresolvedCount} unresolved`}
            size="small"
            sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: 'error.main', color: '#fff' }}
          />
        )}
      </Box>

      {/* New comment input */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: getAvatarColor(myProfile?._id) }}>
          {myProfile?.name?.[0]?.toUpperCase() || '?'}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={1}
            maxRows={4}
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
            }}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }}
          />
          {newComment.trim() && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
              <Button size="small" onClick={() => setNewComment('')} sx={{ mr: 0.5, color: 'text.secondary' }}>Cancel</Button>
              <Button size="small" variant="contained" onClick={handleSubmit}>Comment</Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Comments list */}
      {(comments || []).map((comment) => {
        const isAuthor = myProfile?._id === comment.author?._id;
        return (
          <Box
            key={comment._id}
            sx={{
              display: 'flex', gap: 1, mb: 1.5, py: 1, px: 1,
              borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
              bgcolor: comment.resolved ? 'action.hover' : 'background.paper',
              opacity: comment.resolved ? 0.65 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            <Avatar sx={{ width: 26, height: 26, fontSize: 10, mt: 0.25, bgcolor: getAvatarColor(comment.author?._id) }}>
              {comment.author?.name?.[0]?.toUpperCase() || '?'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {comment.author?.name || 'Unknown'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>
                  {formatDate(comment.createdAt)}
                </Typography>
                {comment.resolved && (
                  <Chip label="Resolved" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 600, bgcolor: 'success.main', color: '#fff' }} />
                )}
              </Box>
              <Typography variant="body2" sx={{
                color: 'text.primary', fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                ...(comment.resolved && { textDecoration: 'line-through', color: 'text.disabled' }),
              }}>
                {comment.text}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'center' }}>
              <IconButton size="small" onClick={() => handleResolve(comment)} title={comment.resolved ? 'Unresolve' : 'Resolve'}
                sx={{ width: 24, height: 24, color: comment.resolved ? 'success.main' : 'text.disabled' }}>
                {comment.resolved ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
              </IconButton>
              {isAuthor && (
                <IconButton size="small" onClick={() => handleDelete(comment._id)} title="Delete"
                  sx={{ width: 24, height: 24, color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
          </Box>
        );
      })}

      {(comments || []).length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', py: 2 }}>
          No comments yet
        </Typography>
      )}
    </Box>
  );
};
